import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

interface ThemeSnap {
  sidebarBg: string;
  sidebarAccent: string;
  pageBg: string;
  primaryColor: string;
  fontFamily: string;
  logoUrl: string;
  nomeCamara: string;
  subtituloSidebar: string;
}

const DEFAULTS: ThemeSnap = {
  sidebarBg: '#0f172a',
  sidebarAccent: '#18181b',
  pageBg: '#f8f7f4',
  primaryColor: '#0f172a',
  fontFamily: 'Inter',
  logoUrl: '',
  nomeCamara: 'Câmara Municipal',
  subtituloSidebar: 'SIADAP · Ciclo 2025',
};

export default function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [snap, setSnap] = useState<ThemeSnap>(DEFAULTS);

  useEffect(() => {
    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'app_theme')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          try {
            const parsed = JSON.parse(data.value) as Partial<ThemeSnap>;
            setSnap({ ...DEFAULTS, ...parsed });
          } catch { /* usa defaults */ }
        }
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await signIn(email, password);
    setLoading(false);
    if (err) {
      setError('Email ou password incorretos. Tente novamente.');
    } else {
      navigate('/funcionarios');
    }
  };

  // Determina se o painel lateral é claro ou escuro
  const isLight = snap.sidebarBg === '#ffffff' || snap.sidebarBg === '#f4f4f5';
  const panelText = isLight ? '#18181b' : '#ffffff';
  const panelMuted = isLight ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)';

  return (
    <div
      className="min-h-screen flex"
      style={{ backgroundColor: snap.pageBg, fontFamily: `'${snap.fontFamily}', system-ui, sans-serif` }}
    >
      {/* Painel esquerdo — identidade */}
      <div
        className="hidden lg:flex w-[420px] flex-shrink-0 flex-col justify-between p-10 relative overflow-hidden"
        style={{ backgroundColor: snap.sidebarBg }}
      >
        {/* Decoração de fundo */}
        <div
          className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full opacity-10"
          style={{ backgroundColor: snap.sidebarAccent }}
        />
        <div
          className="absolute top-1/3 -right-16 w-48 h-48 rounded-full opacity-8"
          style={{ backgroundColor: snap.sidebarAccent }}
        />

        {/* Logo / Nome */}
        <div className="relative z-10">
          {snap.logoUrl ? (
            <img src={snap.logoUrl} alt="Logo" className="h-10 object-contain mb-8" />
          ) : (
            <div className="flex items-center gap-3 mb-8">
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ backgroundColor: `${snap.sidebarAccent}25` }}
              >
                <i className="ri-government-line text-lg" style={{ color: snap.sidebarAccent }} />
              </div>
              <div>
                <p className="text-[15px] font-bold leading-tight" style={{ color: panelText }}>
                  {snap.nomeCamara}
                </p>
                <p className="text-[12px] leading-tight mt-0.5" style={{ color: panelMuted }}>
                  {snap.subtituloSidebar}
                </p>
              </div>
            </div>
          )}

          <div className="mt-12">
            <h2
              className="text-[32px] font-bold leading-tight mb-3"
              style={{ color: panelText }}
            >
              RH
            </h2>
            <p className="text-[14px] leading-relaxed" style={{ color: panelMuted }}>
              Plataforma integrada de avaliação SIADAP e gestão de carreiras dos trabalhadores.
            </p>
          </div>

          {/* Features */}
          <div className="mt-10 space-y-4">
            {[
              { icon: 'ri-bar-chart-line', label: 'Progressões automáticas por pontos' },
              { icon: 'ri-award-line', label: 'Avaliações SIADAP multi-período' },
              { icon: 'ri-team-line', label: 'Gestão completa de funcionários' },
              { icon: 'ri-book-open-line', label: 'Gestão das formações dos funcionários' },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-3">
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ backgroundColor: `${snap.sidebarAccent}20` }}
                >
                  <i className={`${f.icon} text-sm`} style={{ color: snap.sidebarAccent }} />
                </div>
                <p className="text-[13px]" style={{ color: panelMuted }}>{f.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer do painel */}
        <div className="relative z-10">
          <p className="text-[11px]" style={{ color: `${panelMuted}` }}>
            &copy; {new Date().getFullYear()} {snap.nomeCamara} — Acesso restrito
          </p>
        </div>
      </div>

      {/* Painel direito — formulário */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">

          {/* Mobile: logo */}
          <div className="lg:hidden text-center mb-8">
            {snap.logoUrl ? (
              <img src={snap.logoUrl} alt="Logo" className="h-10 object-contain mx-auto mb-3" />
            ) : (
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3"
                style={{ backgroundColor: snap.primaryColor }}
              >
                <i className="ri-government-line text-white text-xl" />
              </div>
            )}
            <h1 className="text-[18px] font-bold text-zinc-900">{snap.nomeCamara}</h1>
            <p className="text-[13px] text-zinc-400 mt-0.5">{snap.subtituloSidebar}</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl border border-zinc-100 p-8">
            <div className="mb-6">
              <h2 className="text-[20px] font-bold text-zinc-900 tracking-tight">Iniciar sessão</h2>
              <p className="text-[13px] text-zinc-400 mt-1">Introduza as suas credenciais para aceder</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-zinc-600 uppercase tracking-widest mb-2">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-zinc-400">
                    <i className="ri-mail-line text-sm" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@camara.pt"
                    required
                    className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 focus:border-zinc-400 transition-colors bg-zinc-50/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-zinc-600 uppercase tracking-widest mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-zinc-400">
                    <i className="ri-lock-line text-sm" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full pl-9 pr-10 py-2.5 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 focus:border-zinc-400 transition-colors bg-zinc-50/50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-zinc-400 hover:text-zinc-600 cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2.5 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-red-500 text-sm" />
                  </div>
                  <p className="text-[13px] text-red-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 text-white text-[13px] font-semibold rounded-lg transition-all disabled:opacity-60 cursor-pointer whitespace-nowrap mt-2"
                style={{ backgroundColor: snap.primaryColor }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    A entrar...
                  </span>
                ) : (
                  'Entrar'
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-[11px] text-zinc-400 mt-5">
            Acesso restrito a utilizadores autorizados
          </p>
        </div>
      </div>
    </div>
  );
}
